import React, { useState, useRef } from 'react';
import { View, Text } from 'react-native';
import i18n from 'i18n-js';

import { connect } from 'react-redux';

import Loading from '@components/loading';
import { scale, width } from '@components/frame';
import { localTime, isPhone, isWeb, formatOEENumber } from '@helpers';
import {
  machineStatisticsOeeTeep,
  machineStatisticsOeeTeepMachineList,
  getMachineStatsOeeTeepPerPeriod,
} from '../../../../reducers/statistics';
import { getBoxShadowStyle } from '@styles/shadow';
import { font } from '@styles/fonts';

import Dash from './components/dash';
import Line from './components/line';
import Chart from './components/chart/index';
import { getOEEEffectivenessLabel } from './components/chart/components/helpers';
import { getPeriodStep } from '@components/machines/components/eventsChart/helpers/period';
import { useMobileChartReset } from '@components/machines/components/eventsChart/hooks/useMobileChartReset';
import FormulaCircles from './components/FormulaCircles';
import Arrow from '@components/common/arrow';
import { setChartData, setSelectedChartDate } from '@reducers/machinesV2';
import { useHandleParams } from '@v2/pages/machines/hooks/useHandleParams';
import { InlineErrorBoundary } from '@v2/components/errorBoundary';
import { PERFORMANCE_CHART_BLOCK_WIDTH, SCREEN_BORDER_RADIUS, TEST_IDS } from '@v2/pages/common/constants/base.constants';
import { useDeviceSize } from '@v2/pages/common/hooks/useDeviceSize';
import { useManageUtilizationStats } from './hooks/useManageUtilizationStats';

const Content = ({ isError, loading, machineStatistics, blockWidth, C, elementWidth, padding }) => {
  const [labelWidth, setLabelWidth] = useState(0);

  if (isError) {
    return (
      <View style={{ alignItems: 'center', justifyContent: 'center', width: '100%', flex: 1 }}>
        <Text>{i18n.t('No data')}</Text>
      </View>
    );
  }

  if (loading) {
    return (
      <View style={{ alignItems: 'center', justifyContent: 'center', width: '100%', flex: 1 }}>
        <Loading {...{ background: 'transparent' }} />
      </View>
    );
  }

  const renderArrayRaw = [
    {
      value: machineStatistics.totalQualitySecs,
      name: 'Good Production',
      backgroundColor: 'OEE/Light/Factor',
      color: 'Label/Light/Primary',
      fromStart: true,
    },
    {
      value: machineStatistics.totalQualityLossSecs,
      name: 'Quality Loss',
      backgroundColor: 'OEE/Light/Loss',
      color: 'Label/Light/Error',
      alwaysShow: true,
    },
    {
      value: machineStatistics.totalPerformanceSecs,
      name: 'Ideal Speed',
      backgroundColor: 'OEE/Light/Factor',
      color: 'Label/Light/Primary',
      fromStart: true,
    },
    {
      value: machineStatistics.totalSpeedLossSecs,
      name: 'Speed Loss',
      backgroundColor: 'OEE/Light/Loss',
      color: 'Label/Light/Error',
      alwaysShow: true,
    },
    {
      value: machineStatistics.totalActiveSecs,
      name: 'Active',
      backgroundColor: 'OEE/Light/Factor',
      color: 'Label/Light/Primary',
      fromStart: true,
    },
    {
      value: machineStatistics.totalSlowChangeLossSecs,
      name: 'Slow Change Loss',
      backgroundColor: 'OEE/Light/Loss',
      color: 'Label/Light/Error',
    },
    {
      value: machineStatistics.totalChangeLossSecs,
      name: 'Change Loss',
      backgroundColor: 'OEE/Light/Loss',
      color: 'Label/Light/Error',
    },
    {
      value: machineStatistics.totalSlowSetupLossSecs,
      name: 'Slow Setup Loss',
      backgroundColor: 'OEE/Light/Loss',
      color: 'Label/Light/Error',
    },
    {
      value: machineStatistics.totalSetupLossSecs,
      name: 'Setup Loss',
      backgroundColor: 'OEE/Light/Loss',
      color: 'Label/Light/Error',
    },
    {
      value: machineStatistics.totalDisturbancesSecs,
      name: 'Disturbances',
      backgroundColor: 'OEE/Light/Loss',
      color: 'Label/Light/Error',
    },
    {
      value: machineStatistics.totalScheduledSecs,
      name: 'Productive',
      backgroundColor: 'OEE/Light/All Time',
      color: 'Label/Light/Primary',
      fromStart: true,
    },
    {
      value: machineStatistics.totalUnScheduledSecs,
      name: 'Un-Scheduled',
      backgroundColor: 'OEE/Light/Loss',
      color: 'Label/Light/Error',
    },
    {
      value: machineStatistics.totalNotScheduledSecs,
      name: 'Not Scheduled',
      backgroundColor: 'OEE/Light/Loss',
      color: 'Label/Light/Error',
    },
    {
      value: machineStatistics.totalDurationSecs,
      name: 'All Time',
      backgroundColor: 'OEE/Light/All Time',
      color: 'Label/Light/Primary',
      fromStart: true,
    },
  ];

  const renderArray = renderArrayRaw.filter((i) => i.value || i.fromStart || i.alwaysShow);
  const pxInSecond = elementWidth / machineStatistics.totalDurationSecs || 0;
  let offset = 0;

  const OEEDashPositionQuality = machineStatistics.totalQualitySecs * pxInSecond;
  const OEEDashPositionPlan = machineStatistics.totalScheduledSecs * pxInSecond;

  const renderArrayNames = renderArray.map((i) => i.name);
  const plannedProductiveIndex = renderArrayNames.indexOf('Productive');
  const totalDashTopPosition = renderArray.length * scale(37) - scale(12);
  const totalDashPlannedProductivePositionTop = (renderArray.length - plannedProductiveIndex) * scale(37) - scale(12);
  const totalDashPlannedProductivePositionSize = totalDashTopPosition - totalDashPlannedProductivePositionTop + scale(105);

  const effectiveness = getOEEEffectivenessLabel(machineStatistics.oee);
  const effectivenessWidth = machineStatistics.totalScheduledSecs * pxInSecond || 0;
  const effectivenessLabelLeft = Math.max(0, Math.min(
    effectivenessWidth - labelWidth,
    machineStatistics.oee * effectivenessWidth - labelWidth / 2,
  ));

  const effectivenessBorderRadius = scale(4);
  const effectivenessConfig = {
    poor: {
      borderRadiusConfig: {
        borderTopLeftRadius: effectivenessBorderRadius,
        borderBottomLeftRadius: effectivenessBorderRadius,
      },
      backgroundColor: 'OEE/Light/Score Poor',
      width: 0.4 * effectivenessWidth - 1,
      text: `0-40% ${i18n.t('Poor')}`,
    },
    fair: {
      borderRadiusConfig: null,
      backgroundColor: 'OEE/Light/Score Fair',
      width: 0.2 * effectivenessWidth - 1,
      text: `40-60% ${i18n.t('Fair')}`,
    },
    good: {
      borderRadiusConfig: null,
      backgroundColor: 'OEE/Light/Score Good',
      width: 0.15 * effectivenessWidth - 1,
      text: `60-75% ${i18n.t('Good')}`,
    },
    excellent: {
      borderRadiusConfig: null,
      backgroundColor: 'OEE/Light/Score Excellent',
      width: 0.1 * effectivenessWidth - 1,
      text: `75-85% ${i18n.t('Excellent')}`,
    },
    worldClass: {
      borderRadiusConfig: {
        borderTopRightRadius: effectivenessBorderRadius,
        borderBottomRightRadius: effectivenessBorderRadius,
      },
      backgroundColor: 'OEE/Light/Score World Class',
      width: 0.15 * effectivenessWidth,
      text: `85-100% ${i18n.t('World Class')}`,
    },
  };

  return (
    <View style={{ paddingHorizontal: padding }}>
      <View style={{ position: 'relative', width: elementWidth }}>
        <View style={{ position: 'absolute', right: 0, top: scale(17), zIndex: 1 }}>
          <Dash {...{ height: scale(37) * renderArray.length + scale(180), C }} />
        </View>

        <View
          style={{
            position: 'absolute',
            left: OEEDashPositionQuality > 1 ? OEEDashPositionQuality - 1 : OEEDashPositionQuality,
            top: totalDashTopPosition,
            zIndex: 1,
          }}
        >
          <Dash {...{ height: scale(90), C }} />
        </View>

        <View
          style={{
            position: 'absolute',
            left: OEEDashPositionPlan > 1 ? OEEDashPositionPlan - 1 : OEEDashPositionPlan,
            top: totalDashPlannedProductivePositionTop,
            zIndex: 1,
          }}
        >
          <Dash {...{ height: totalDashPlannedProductivePositionSize, C }} />
        </View>

        <View
          style={{
            position: 'absolute',
            left: OEEDashPositionQuality > 1 ? OEEDashPositionQuality - 1 : OEEDashPositionQuality,
            top: renderArray.length * scale(37) - scale(12),
            zIndex: 1,
          }}
        >
          <Dash {...{ height: scale(90), C }} />
        </View>

        <View style={{ flexDirection: 'column-reverse', zIndex: 100 }}>
          {renderArray.map((i, index) => {
            const prev = renderArray[index - 1];

            if (prev) {
              if (prev.fromStart) {
                offset = prev.value;
              } else {
                offset += prev.value;
              }
            }
            return (
              <View key={i.name} style={{ zIndex: i.fromStart ? 100 : 10 }}>
                <Line {...{ ...i, seconds: i.value, offset, zIndex: 1000, pxInSecond, C, elementWidth }} />
              </View>
            );
          })}
        </View>

        <View style={{ marginTop: scale(20), zIndex: 100, position: 'relative' }}>
          <View
            style={{
              width: '100%',
              height: scale(21),
              justifyContent: 'center',
              zIndex: 100,
            }}
          >
            <View
              style={{
                width: effectivenessWidth,
                height: scale(21),
                borderRadius: scale(4),
                backgroundColor: C('Background/Light/Chart'),
                overflow: 'hidden',
                position: 'absolute',
              }}
            >
              <View
                style={{
                  width: `${machineStatistics.oee * 100}%`,
                  height: scale(21),
                  backgroundColor: C('OEE/Light/OEE'),
                  position: 'absolute',
                }}
              />
            </View>
            <Text numberOfLines={1} style={[font['Button 1'], { color: C('Label/Light/Primary'), marginLeft: scale(4) }]}>
              {i18n.t('OEE')} {String(formatOEENumber(machineStatistics.oee * 100)).replace('.', ',')}%
            </Text>
          </View>
          <Text style={[font['Caption 2'], { color: C('Label/Light/Tertiary'), marginLeft: scale(4) }]}>
            {i18n.t('Overall Equipment Effectiveness')}
          </Text>
        </View>

        <View style={{ marginTop: scale(16), zIndex: 100, position: 'relative' }}>
          <View style={{ width: effectivenessWidth, position: 'relative', height: scale(18), marginBottom: scale(2) }}>
            {effectiveness && (
              <Text
                onLayout={(e) => setLabelWidth(e.nativeEvent.layout.width)}
                style={[
                  font['Caption 1'],
                  {
                    color: C(effectiveness.color),
                    position: 'absolute',
                    bottom: 0,
                    opacity: labelWidth ? 1 : 0,
                    left: effectivenessLabelLeft,
                  },
                ]}
              >
                {effectiveness.label}
              </Text>
            )}
          </View>
          <View style={{ width: effectivenessWidth, height: scale(4), flexDirection: 'row', gap: 1 }}>
            {Object.entries(effectivenessConfig).map(([key, config]) => (
              <View
                key={key}
                style={[
                  {
                    width: config.width,
                    height: scale(4),
                    backgroundColor: C(config.backgroundColor),
                  },
                  config.borderRadiusConfig,
                ]}
              />
            ))}
          </View>

          <View style={{ marginTop: scale(5), marginLeft: 1 }}>
            {Object.entries(effectivenessConfig).map(([key, config]) => (
              <View
                key={`${key}-effectiveness-explanation`}
                style={[
                  {
                    flexDirection: 'row',
                    alignItems: 'center',
                    gap: scale(5),
                    height: scale(16),
                  },
                  config.borderRadiusConfig,
                ]}
              >
                <View style={{ width: scale(4), height: scale(4), borderRadius: scale(4), backgroundColor: C(config.backgroundColor) }} />
                <Text style={[font['Caption 2'], { color: C('Label/Light/Tertiary') }]}>{config.text}</Text>
              </View>
            ))}
          </View>
        </View>

        <View style={{ marginTop: scale(14), zIndex: 100, position: 'relative' }}>
          <View
            style={{
              width: machineStatistics.totalDurationSecs * pxInSecond || 0,
              height: scale(21),
              borderRadius: scale(4),
              backgroundColor: C('Background/Light/Chart'),
              overflow: 'hidden',
              zIndex: 100,
              justifyContent: 'center',
            }}
          >
            <View
              style={{
                width: `${machineStatistics.teep * 100}%`,
                height: scale(21),
                backgroundColor: C('OEE/Light/TEEP Chart'),
                position: 'absolute',
              }}
            />
            <Text numberOfLines={1} style={[font['Button 1'], { color: C('Label/Light/Primary'), marginLeft: scale(4) }]}>
              {i18n.t('Net Utilization')} {String(formatOEENumber(machineStatistics.teep * 100)).replace('.', ',')}%
            </Text>
          </View>
          <Text style={[font['Caption 2'], { color: C('Label/Light/Tertiary'), marginLeft: scale(4) }]}>
            {i18n.t('Total Effective Equipment Performance')}
          </Text>
        </View>
      </View>
    </View>
  );
};

const Utilization = (props) => {
  const {
    machineStatisticsOeeTeep,
    machineStatisticsOeeTeepMachineList,
    C,
    getMachineStatsOeeTeepPerPeriod,
    changeDate,
    chartData: data,
    setChartData: setData,
    selectedChartDate,
    setSelectedChartDate,
    selectedTimeCounter,
  } = props;

  const { id, timePeriod: selectedTime } = useHandleParams();
  const { IS_NARROW_WEB } = useDeviceSize();

  const startPageDate = useRef(localTime().format());
  const selectedDate = selectedChartDate || startPageDate.current;

  const { resetMobileChartCount, setSelectedChartDateWithReset: setSelectedChartDateFromCalendar } = useMobileChartReset(setSelectedChartDate);

  const { count: periodCount, unit: periodUnit } = getPeriodStep(selectedTime);

  const { machineStatistics, loading, isError } = useManageUtilizationStats({
    id,
    selectedDate,
    selectedTime,
    selectedTimeCounter,
    machineStatisticsOeeTeep,
    machineStatisticsOeeTeepMachineList,
  });

  const blockWidth = isPhone && !IS_NARROW_WEB ? width : scale(PERFORMANCE_CHART_BLOCK_WIDTH, false);
  const padding = scale(18);
  const elementWidth = blockWidth - padding * 2;
  const chartWidth = elementWidth - scale(60);

  const arrowStyles = { paddingTop: scale(122), minWidth: scale(50) };

  const momentStart = selectedChartDate ? localTime(selectedChartDate) : localTime();
  const shouldDisplayRightArrow = !localTime(momentStart).add(periodCount, periodUnit).isAfter(localTime());

  return (
    <View
      style={{
        width: '100%',
        alignItems: 'center',
      }}
    >
      <View style={{ flexDirection: 'row', flex: 1, width: '100%', justifyContent: 'center' }}>
        {isWeb && (
          <View style={arrowStyles}>
            <Arrow {...{ C, onPress: () => changeDate('subtract'), testID: TEST_IDS.utilizationChartNavigation.leftArrow }} />
          </View>
        )}
        <View style={{ width: blockWidth, gap: scale(12) }}>
          <InlineErrorBoundary screenName="UtilizationChartBlock">
            <View
              testID={TEST_IDS.utilization.chartBlock}
              style={[
                {
                  backgroundColor: C('Background/Light/Field'),
                  paddingBottom: scale(18),
                },
                isWeb && getBoxShadowStyle({ type: 'Narrow', C }),
                (!isPhone || IS_NARROW_WEB) && { borderRadius: scale(SCREEN_BORDER_RADIUS), overflow: 'hidden' },
              ]}
            >
              <Chart
                {...{
                  setData,
                  C,
                  selectedDate,
                  padding,
                  chartWidth,
                  elementWidth,
                  getMachineStatsOeeTeepPerPeriod,
                  id,
                  setSelectedChartDate,
                  setSelectedChartDateFromCalendar,
                  resetMobileChartCount,
                  machineStatistics,
                  machineStatisticsLoading: loading,
                  machineStatisticsIsError: isError,
                }}
              />
            </View>
          </InlineErrorBoundary>

          <InlineErrorBoundary screenName="UtilizationContentBlock">
            <View
              testID={TEST_IDS.utilization.contentBlock}
              style={[
                {
                  backgroundColor: C('Background/Light/Field'),
                  minHeight: scale(450),
                  paddingTop: scale(10),
                  paddingBottom: scale(18),
                },
                isWeb && getBoxShadowStyle({ type: 'Narrow', C }),
                (!isPhone || IS_NARROW_WEB) && { borderRadius: scale(SCREEN_BORDER_RADIUS), overflow: 'hidden' },
              ]}
            >
              <Content {...{ ...props, loading, isError, blockWidth, machineStatistics, C, padding, elementWidth }} />
            </View>
          </InlineErrorBoundary>

          {!loading && !isError && (
            <InlineErrorBoundary screenName="UtilizationFormulaBlock">
              <View testID={TEST_IDS.utilization.formulaCircles}>
                <FormulaCircles {...{ C, machineStatistics }} />
              </View>
            </InlineErrorBoundary>
          )}
        </View>
        {isWeb && <View style={arrowStyles}>{shouldDisplayRightArrow && <Arrow {...{ C, rotate: 180, onPress: () => changeDate('add'), testID: TEST_IDS.utilizationChartNavigation.rightArrow }} />}</View>}
      </View>
    </View>
  );
};

export default connect(
  ({ machinesV2: { chartData, selectedChartDate, selectedTimeCounter } }) => ({ chartData, selectedChartDate, selectedTimeCounter }),
  {
    machineStatisticsOeeTeep,
    machineStatisticsOeeTeepMachineList,
    getMachineStatsOeeTeepPerPeriod,
    setChartData,
    setSelectedChartDate,
  }
)(Utilization);
